import type { OpenSessionEventOptions } from '../AppEvents';
import type { SessionAppRequirement} from './SessionManager';
import type { LockContext } from '@easterngraphics/wcf/modules/utils/async';

import isEqual from 'lodash/isEqual';

import { appEvents } from '../AppEvents';
import { getGatekeeperId } from '../hooks/GatekeeperId';

import { executeCallbacks, getTaskQueue, SessionManager } from './SessionManager';

import { pConServiceProvider } from '@egr/xbox/pcon-login-api/ServicesProvider';
import { methodWithDebugTimer } from '@egr/xbox/utils/Debug';
import { getLocale } from '@egr/xbox/utils/Language';
import { limitConcurrentCalls } from '@egr/xbox/utils/Promise';
import { getEnvVar } from '@egr/xbox/utils/ReactScriptHelper';
import { captureException } from '@egr/xbox/utils/errors/Error';

import { type SessionResponse, type OpenSessionOptions, NetworkError, GatekeeperClient } from '@easterngraphics/gatekeeper-lib';

export type GatekeeperSessionOptions = OpenSessionOptions;

type GatekeeperSessionResponse = SessionResponse;
/**
 * @param updatedSessionOptions If this value is set, the specified options are sent to the gatekeeper instead of the original ones used for the initial request
 */
export type GatekeeperRequestRetryCallback = (updatedSessionOptions?: GatekeeperSessionOptions) => void;

/**
 * This function can be used to handle application specific errors in the communication with the gatekeeper.
 * (e.g. if a pCon.login access token expires, the token can be renewed and than the request can be retried via the
 * callback provided to this function)
 *
 * @param error the error hat occurred when requesting a new session
 * @param retry a callback with which the original request can be executed again
 * @param reject the reject function of the original promise return to the function which requested the new session
 *
 * @returns true if the error was handled and false otherwise
 */
type RequestErrorHandler = (error: Error, retry: GatekeeperRequestRetryCallback, reject: (error: Error) => void) => Promise<boolean>;

const MAX_SESSION_AGE: number = 14400000; // 4h

export class GatekeeperManager<App extends SessionAppRequirement = SessionAppRequirement> extends SessionManager<App> {
    public readonly requestErrorHandlers: Array<RequestErrorHandler> = [];

    protected sessionCreationTime: undefined | number;
    protected sessionReusabilityOptions: Record<string, unknown> | undefined;

    public constructor(
        app: App,
        protected sessionOptionsProvider?: () => Promise<GatekeeperSessionOptions>,
        protected gatekeeperClient: GatekeeperClient = new GatekeeperClient({
            serviceProvider: pConServiceProvider,
        })
    ) {
        super(app);
    }

    public async preInitializeEaiwsSession(primaryDataLanguage?: string): Promise<void> {
        const lockContext: LockContext = await this.getSessionLockContext();
        try {
            await this.startEaiwsSession(primaryDataLanguage);
            await this.setLanguage(primaryDataLanguage, this.app.eaiwsBaskets.basket);
            void this.app.eaiwsBaskets.session.session.configureSessionLog(true);
            if (this.app.eaiwsBaskets.session.isValid) {
                const {queue: postConnectQueue, callbacks: callbacksPostConnectQueue} = getTaskQueue<[OpenSessionEventOptions]>('preInitializeEaiwsSession.postConnect');
                appEvents.session.trigger({
                    type: 'pre-initialization',
                    tasks: {
                        postConnect: postConnectQueue
                    },
                    primaryDataLanguage
                });

                await executeCallbacks('preInitializeEaiwsSession', 'postConnect', callbacksPostConnectQueue, [{}]);
            }
        } finally {
            lockContext.unlock();
        }
    }

    @limitConcurrentCalls()
    protected async startEaiwsSession(primaryDataLanguage?: string): Promise<void> {
        const gatekeeperSessionOptions: GatekeeperSessionOptions = (await this.sessionOptionsProvider?.()) ?? {};
        let requestNewSession: boolean = !(await this.shouldReuseSession(primaryDataLanguage, gatekeeperSessionOptions));

        // Note: since the servers also need to be maintained, we should not use a session for a too long period of time.
        // Therefore we rather ask for a new session to give the gatekeeper the possibility to send us to another server.
        if (!requestNewSession && Date.now() > ((this.sessionCreationTime ?? 0) + MAX_SESSION_AGE)) {
            requestNewSession = true;
        }

        if (requestNewSession) {
            const [response, usedSessionOptions] = await this.requestSession(primaryDataLanguage, gatekeeperSessionOptions);
            this.sessionCreationTime = Date.now();

            this.app.eaiwsBaskets.session.connect(
                response.server.slice(0, -1), // remove trailing slash
                response.sessionId,
                response.keepAliveInterval * 1000 // the keepAliveInterval is given in seconds
            );

            this.sessionReusabilityOptions = await this.getSessionReusabilityOptions(primaryDataLanguage, usedSessionOptions ?? {});
        }
    }

    protected override async endEaiwsSession(): Promise<void> {
        if (this.app.eaiwsBaskets.session.isValid) {
            try {
                await this.app.eaiwsBaskets.session.session.loadEmptySession();
            } catch {
                this.app.eaiwsBaskets.session.disconnect();
            }
        }
    }

    @methodWithDebugTimer('gatekeeper request')
    protected async requestSession(
        primaryDataLanguage?: string,
        gatekeeperSessionOptions?: GatekeeperSessionOptions,
        gatekeeperIdOverride?: string,
    ): Promise<[GatekeeperSessionResponse, GatekeeperSessionOptions | undefined]> {
        return new Promise<[GatekeeperSessionResponse, GatekeeperSessionOptions | undefined]>((
            resolve: (response: [GatekeeperSessionResponse, GatekeeperSessionOptions | undefined]) => void,
            reject: (error: Error) => void
        ): void => {
            const request: GatekeeperRequestRetryCallback = async (updatedSessionOptions?: GatekeeperSessionOptions): Promise<void> => {
                try {
                    const sessionOptions: GatekeeperSessionOptions | undefined = updatedSessionOptions ?? gatekeeperSessionOptions;
                    const gatekeeperId = gatekeeperIdOverride ?? await getGatekeeperId();

                    let gatekeeperSessionSettings: GatekeeperSessionOptions['gatekeeperSessionSettings'] | undefined;
                    const reply: GatekeeperSessionResponse = await this.gatekeeperClient.openSession(
                        gatekeeperId,
                        {
                            locale: getLocale(primaryDataLanguage),
                            applicationId: getEnvVar('GATEKEEPER_APPLICATION_ID'),
                            gatekeeperSessionSettings,
                            ...sessionOptions
                        }
                    );

                    resolve([reply, sessionOptions]);
                } catch (error) {
                    if (error instanceof NetworkError) {
                        this.app.handleNetworkError(request);
                    } else {
                        for (const errorHandler of this.requestErrorHandlers) {
                            try {
                                const handled: boolean = await errorHandler(error, request, reject);
                                if (handled) {
                                    return;
                                }
                            } catch (handlerError) {
                                captureException(handlerError, 'debug');
                                reject(handlerError as Error);
                                return;
                            }
                        }
                        reject(error as Error);
                    }
                }
            };

            request();
        });
    }

    // Note: the `primaryDataLanguage` argument should be required
    protected async shouldReuseSession(primaryDataLanguage: string | undefined, gatekeeperSessionOptions: GatekeeperSessionOptions): Promise<boolean> {
        if (!this.app.eaiwsBaskets.session.isValid || this.sessionReusabilityOptions == null) {
            return false;
        }

        return isEqual(
            this.sessionReusabilityOptions,
            await this.getSessionReusabilityOptions(primaryDataLanguage, gatekeeperSessionOptions)
        );
    }

    // Note: the `primaryDataLanguage` argument should be required
    protected async getSessionReusabilityOptions(
        primaryDataLanguage: string | undefined,
        gatekeeperSessionOptions: GatekeeperSessionOptions
    ): Promise<Record<string, unknown>> {

        return {
            gatekeeperId: await getGatekeeperId(),
            gatekeeperSessionOptions: gatekeeperSessionOptions,
            primaryDataLanguage
        };
    }
}
